<script lang="ts">
  import type { ApiField } from '../lib/data-tables-api';
  import type { KanbanCardConfig, CardSize } from '../lib/kanban-config';
  import { loadPresets, savePreset, deletePreset, type KanbanPreset } from '../lib/kanban-config';
  import { dndzone, type DndEvent } from 'svelte-dnd-action';
  import { flip } from 'svelte/animate';
  import Icon from './Icon.svelte';
  import FieldDisplay from '../lib/fields/FieldDisplay.svelte';

  let {
    slug,
    availableFields,
    config,
    sampleValues = {},
    onSave,
    onClose,
  }: {
    /** Slug таблиці — для збереження пресетів. */
    slug: string;
    /** Усі поля з яких можна обирати (built-in + custom, не linked_summary). */
    availableFields: ApiField[];
    config: KanbanCardConfig;
    /** Значення реального запису для live-preview (опційно). */
    sampleValues?: Record<string, unknown>;
    onSave: (c: KanbanCardConfig) => void;
    onClose: () => void;
  } = $props();

  /** DnD-елемент: id = fieldKey (унікальний у конфігу). */
  interface Row {
    id: string;
    showLabel: boolean;
  }

  let primaryFieldKey = $state(config.primaryFieldKey);
  let rows = $state<Row[]>(config.fields.map((f) => ({ id: f.key, showLabel: f.showLabel })));
  let cardSize = $state<CardSize>(config.cardSize);

  let presets = $state<KanbanPreset[]>(loadPresets(slug));
  let newPresetName = $state('');

  const fieldByKey = $derived(new Map(availableFields.map((f) => [f.fieldKey, f])));
  const fieldLabel = (key: string) => fieldByKey.get(key)?.label ?? key;

  const unusedFields = $derived(
    availableFields.filter(
      (f) => f.fieldKey !== primaryFieldKey && !rows.some((x) => x.id === f.fieldKey),
    ),
  );

  function addField() {
    const next = unusedFields[0];
    if (next) rows = [...rows, { id: next.fieldKey, showLabel: true }];
  }
  function removeField(id: string) {
    rows = rows.filter((r) => r.id !== id);
  }
  function changeField(idx: number, key: string) {
    rows[idx] = { ...rows[idx]!, id: key };
  }
  function toggleLabel(idx: number) {
    rows[idx] = { ...rows[idx]!, showLabel: !rows[idx]!.showLabel };
  }

  function onDndConsider(e: CustomEvent<DndEvent<Row>>) {
    rows = e.detail.items;
  }
  function onDndFinalize(e: CustomEvent<DndEvent<Row>>) {
    rows = e.detail.items;
  }

  function currentConfig(): KanbanCardConfig {
    return {
      primaryFieldKey,
      fields: rows.map((r) => ({ key: r.id, showLabel: r.showLabel })),
      cardSize,
    };
  }

  function apply() {
    onSave(currentConfig());
  }

  /* ── Presets ──────────────────────────────────────────────────────────────── */
  function applyPreset(p: KanbanPreset) {
    primaryFieldKey = p.config.primaryFieldKey;
    rows = p.config.fields.map((f) => ({ id: f.key, showLabel: f.showLabel }));
    cardSize = p.config.cardSize;
  }
  function saveAsPreset() {
    const name = newPresetName.trim();
    if (!name) return;
    presets = savePreset(slug, { name, config: currentConfig() });
    newPresetName = '';
  }
  function removePreset(name: string) {
    presets = deletePreset(slug, name);
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }

  const sizeLabels: Record<CardSize, string> = {
    small: 'Маленький',
    medium: 'Середній',
    large: 'Великий',
  };

  /* ── Live preview ─────────────────────────────────────────────────────────── */
  const previewPrimary = $derived(
    (primaryFieldKey ? fieldByKey.get(primaryFieldKey) : null) ?? availableFields[0] ?? null,
  );
  function previewTitle(): string {
    if (!previewPrimary) return 'Запис';
    const v = sampleValues[previewPrimary.fieldKey];
    return v == null || v === '' ? previewPrimary.label : String(v);
  }
  const previewSize = $derived.by(() => {
    if (cardSize === 'small') return { card: 'p-2.5', title: 'text-[11.5px]', row: 'text-[10px]' };
    if (cardSize === 'large') return { card: 'p-4', title: 'text-[14px]', row: 'text-[12px]' };
    return { card: 'p-3', title: 'text-[12.5px]', row: 'text-[11px]' };
  });
</script>

<svelte:window onkeydown={onKeydown} />

<div
  class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
  role="presentation"
  onclick={onClose}
>
  <div
    class="w-full max-w-[860px] max-h-[90vh] flex flex-col rounded-2xl border border-[var(--color-border-3)] bg-white shadow-2xl overflow-hidden"
    role="dialog"
    onclick={(e) => e.stopPropagation()}
  >
    <!-- Header -->
    <div class="px-6 py-4 border-b border-[var(--color-border-3)] flex items-center justify-between shrink-0">
      <h2 class="text-lg font-bold">Налаштування картки</h2>
      <button type="button" onclick={onClose} class="inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text)] leading-none"><Icon name="close" size={18} /></button>
    </div>

    <!-- Body: config + live preview -->
    <div class="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-[1fr_280px]">
      <!-- Config column -->
      <div class="px-6 py-5 space-y-5 border-b md:border-b-0 md:border-r border-[var(--color-border-3)]">
        <!-- Presets -->
        <div class="rounded-xl border border-[var(--color-border-3)] p-3 space-y-2">
          <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted-2)]">Пресети</div>
          {#if presets.length > 0}
            <div class="flex flex-wrap gap-1.5">
              {#each presets as p (p.name)}
                <span class="inline-flex items-center gap-1 rounded-full border border-[var(--color-border-3)] bg-[var(--color-bg-3)] pl-2.5 pr-1 py-0.5 text-[11px]">
                  <button type="button" onclick={() => applyPreset(p)} class="font-semibold hover:text-[var(--color-pu)]">{p.name}</button>
                  <button type="button" onclick={() => removePreset(p.name)} class="inline-flex items-center text-[var(--color-text-muted-2)] hover:text-rose-500 leading-none" aria-label="Видалити пресет"><Icon name="close" size={13} /></button>
                </span>
              {/each}
            </div>
          {/if}
          <div class="flex items-center gap-2">
            <input
              bind:value={newPresetName}
              placeholder="Назва пресету"
              onkeydown={(e) => { if (e.key === 'Enter') saveAsPreset(); }}
              class="flex-1 rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-[12px]"
            />
            <button type="button" onclick={saveAsPreset} disabled={!newPresetName.trim()}
              class="rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-[11px] font-semibold hover:bg-[var(--color-bg-3)] disabled:opacity-50">
              Зберегти
            </button>
          </div>
        </div>

        <!-- Primary field -->
        <label class="block">
          <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted-2)] mb-1">Заголовок (Поле)</div>
          <select
            bind:value={primaryFieldKey}
            class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
          >
            <option value={null}>— перше доступне —</option>
            {#each availableFields.filter((f) => !rows.some((x) => x.id === f.fieldKey)) as f (f.fieldKey)}
              <option value={f.fieldKey}>{f.label}</option>
            {/each}
          </select>
        </label>

        <!-- Additional fields (drag to reorder) -->
        <div>
          <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted-2)] mb-1.5">Поля картки (перетягни щоб впорядкувати)</div>
          <div
            use:dndzone={{ items: rows, type: 'card-fields', flipDurationMs: 160, dropTargetStyle: { outline: '2px dashed var(--color-pu-2)' } }}
            onconsider={onDndConsider}
            onfinalize={onDndFinalize}
            class="space-y-1.5 min-h-[44px]"
          >
            {#each rows as r, idx (r.id)}
              <div animate:flip={{ duration: 160 }} class="flex items-center gap-2 rounded-lg border border-[var(--color-border-3)] bg-white px-2 py-1.5">
                <span class="inline-flex cursor-grab active:cursor-grabbing text-[var(--color-text-muted-2)] select-none"><Icon name="grip" size={14} /></span>
                <select
                  value={r.id}
                  onchange={(e) => changeField(idx, (e.target as HTMLSelectElement).value)}
                  class="flex-1 rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1.5 text-[12px]"
                >
                  <option value={r.id}>{fieldLabel(r.id)}</option>
                  {#each unusedFields as u (u.fieldKey)}
                    <option value={u.fieldKey}>{u.label}</option>
                  {/each}
                </select>
                <button
                  type="button"
                  onclick={() => toggleLabel(idx)}
                  title={r.showLabel ? 'З підписом' : 'Без підпису'}
                  class="shrink-0 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors
                    {r.showLabel
                      ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]'
                      : 'border-[var(--color-border-3)] bg-white text-[var(--color-text-muted)]'}"
                ><Icon name="tags" size={13} /></button>
                <button type="button" onclick={() => removeField(r.id)}
                  class="shrink-0 inline-flex items-center text-[var(--color-text-muted-2)] hover:text-rose-500 leading-none" aria-label="Прибрати поле"><Icon name="close" size={14} /></button>
              </div>
            {/each}
          </div>
          {#if unusedFields.length > 0}
            <button type="button" onclick={addField}
              class="mt-2 text-[12px] font-semibold text-[var(--color-pu-3)] hover:underline uppercase tracking-wide">
              + Додати поле
            </button>
          {/if}
        </div>

        <!-- Card size -->
        <label class="block">
          <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted-2)] mb-1">Розмір картки</div>
          <select
            bind:value={cardSize}
            class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-3 py-2 text-sm"
          >
            {#each ['small', 'medium', 'large'] as s}
              <option value={s}>{sizeLabels[s as CardSize]}</option>
            {/each}
          </select>
        </label>
      </div>

      <!-- Live preview column -->
      <div class="px-5 py-5 bg-[var(--color-bg-3)]/60">
        <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted-2)] mb-2">Прев'ю</div>
        <div class="rounded-lg border border-[var(--color-border-3)] bg-white {previewSize.card} shadow-sm">
          <div class="{previewSize.title} font-semibold text-[var(--color-text)] mb-1.5 truncate leading-snug">
            {previewTitle()}
          </div>
          {#each rows as r (r.id)}
            {@const f = fieldByKey.get(r.id)}
            {#if f}
              {@const val = sampleValues[f.fieldKey]}
              {@const isEmpty = val == null || val === '' || (Array.isArray(val) && val.length === 0)}
              <div class="flex items-baseline gap-1.5 {previewSize.row} text-[var(--color-text-muted)] mb-0.5 leading-snug">
                {#if r.showLabel}
                  <span class="shrink-0 truncate max-w-[90px]">{f.label}:</span>
                {/if}
                <span class="font-medium text-[var(--color-text)] min-w-0 truncate flex-1">
                  {#if isEmpty}
                    <span class="text-[var(--color-text-muted-2)]">—</span>
                  {:else}
                    <FieldDisplay field={f} value={val} />
                  {/if}
                </span>
              </div>
            {/if}
          {/each}
          {#if rows.length === 0}
            <div class="text-[11px] italic text-[var(--color-text-muted-2)]">Додайте поля зліва…</div>
          {/if}
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-[var(--color-border-3)] flex justify-end gap-2 shrink-0">
      <button type="button" onclick={onClose}
        class="rounded-lg border border-[var(--color-border-3)] px-4 py-2 text-xs font-semibold hover:bg-[var(--color-bg-3)] uppercase tracking-wide">
        Скасувати
      </button>
      <button type="button" onclick={apply}
        class="rounded-lg bg-[var(--color-pu)] text-white px-5 py-2 text-xs font-semibold hover:opacity-90 uppercase tracking-wide">
        Застосувати
      </button>
    </div>
  </div>
</div>
